/**
 * Room-scoped identity storage.
 *
 * Keys used to be a single global `user_id`/`display_name` pair, so joining
 * a second room overwrote the first: going back to the first room then
 * showed you as a different participant. Scoping the keys by room id keeps
 * each room's identity independent.
 */

export interface RoomIdentity {
  userId: string;
  displayName: string;
}

const storageKey = (roomId: string, field: 'user_id' | 'display_name'): string =>
  `room:${roomId}:${field}`;

export const saveRoomIdentity = (roomId: string, userId: string, displayName: string): void => {
  localStorage.setItem(storageKey(roomId, 'user_id'), userId);
  localStorage.setItem(storageKey(roomId, 'display_name'), displayName);
};

export const loadRoomIdentity = (roomId: string): RoomIdentity | null => {
  const userId = localStorage.getItem(storageKey(roomId, 'user_id'));
  const displayName = localStorage.getItem(storageKey(roomId, 'display_name'));

  if (!userId || !displayName) return null;
  return { userId, displayName };
};

export const clearRoomIdentity = (roomId: string): void => {
  localStorage.removeItem(storageKey(roomId, 'user_id'));
  localStorage.removeItem(storageKey(roomId, 'display_name'));
};
